import {
  PDFBool,
  PDFDocument,
  PDFFont,
  PDFForm,
  PDFName,
  PDFNumber,
  PDFPage,
  StandardFonts,
  rgb,
} from 'pdf-lib';

import {
  CONTENT_WIDTH,
  type CellLine,
  type Fonts,
  type LineStyle,
  MARGIN,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  type LayoutBlock as PdfLayoutBlock,
  type LayoutItem as PdfLayoutItem,
  type PlacedBlockFragment as PdfPlacedBlockFragment,
  type PlacedLayout as PdfPlacedLayout,
  type StyleDefaults,
  fontFor,
  placeLayoutItems as placeLayoutItemsGeneric,
  styledLines as styledLinesGeneric,
} from '@kit/pdf-layout';

import { type Art13LanguageLayer, buildArt13Layers } from './art13';
import {
  type SelfDisclosureConfig,
  SelfDisclosureConfigSchema,
} from './config.schema';
import {
  FIELD_SECTION_LABELS,
  FIELD_SECTION_ORDER,
  type FieldSection,
  type LocalizedText,
  type SelfDisclosureField,
  getFieldByKey,
} from './fields';

/**
 * Isomorphic, deterministic PDF render module for the Mieterselbstauskunft
 * (Sanierungs-Spec §2.5, v2). Uses pdf-lib AcroForm fields so the PDF is
 * fillable and signable on mobile viewers. No `server-only`, no Supabase
 * imports – runs in the browser (website CTA) and on the server (settings
 * download / live send).
 *
 * Layout v2 (reference form): tabular – label column left (190 pt), one
 * answer column per person right (merged cell for `scope: 'household'`),
 * framed cells, underlined section headings, a column-head row with the
 * person designation (repeated after page breaks), truth declaration,
 * place/date + signature block per person, Art.-13 layers at the end.
 * Bilingual (`de-en`) renders labels two-line (DE line, EN italic/smaller
 * below) – the table geometry is identical for all three language modes,
 * only row heights grow.
 *
 * Engine: two-phase LayoutBlock rendering from PR 2a – blocks are MEASURED
 * (sum of primitive row heights), then PLACED (`placeLayoutItems`, pure:
 * keep-together, orphan rule, degenerate guard), then DRAWN (`drawRow`
 * interprets the SAME rows). `layoutDocument()` exposes the placement phase
 * without drawing for tests.
 *
 * AcroForm names (R11 – underscore delimiter, NO dot; pdf-lib treats `.` as
 * a field hierarchy separator): `scope: 'person'` → `${key}_p1..p{n}`, plus
 * `${key}_g` for the optional guarantor column (§2); `scope: 'household'` →
 * `${key}`; checkbox_group → `${key}__${optionKey}` (+ `_p{n}` / `_g` for the
 * answer columns); place/date → `sign_place_date_p{n}` / `sign_place_date_g`.
 *
 * Determinism: `PDFDocument.create({ updateMetadata: false })`, fixed
 * `opts.now` (default epoch), `save({ useObjectStreams: false })`. Subject
 * fingerprint v2: `{ v: 2, lang, persons, [guarantor: true], fields }` – the
 * `guarantor` key appears ONLY when set, so guarantor-less configs are
 * byte-identical to before (§2).
 */

// ── Fixed legal wordings ──────────────────────────────────────────────────────

/**
 * Disclaimer footer – EXACTLY ONE placement per language in the PDF.
 * White-label (Spec 2026-07-08 PR A): neutral wording only – no "Locari", no
 * "Generator"/"DSK-Orientierungshilfe" (either would indirectly brand the
 * document, which is distributed standalone into the world).
 */
export const DISCLAIMER_FOOTER =
  'Dieses Formular dient der Vorauswahl von Mietinteressenten. Keine Rechtsberatung.';
export const DISCLAIMER_FOOTER_EN =
  'This form is used to pre-select prospective tenants. No legal advice.';

/** V2 warning notice, verbatim. */
export const V2_WARNING_NOTICE =
  'Keine unaufgeforderte Zusendung nicht erforderlicher Unterlagen!';
export const V2_WARNING_NOTICE_EN =
  'Do not send documents that were not requested and are not required!';

/**
 * X-field usage instruction (Spec 2026-07-13 §2d) – the former checkboxes/radios
 * are now `/Tx` X-fields; this tells the applicant to type an "X" (works on iOS
 * QuickLook, unlike `/Btn`) and doubles as the print-and-tick instruction.
 */
export const X_MARK_INSTRUCTION = 'Zutreffendes bitte mit einem X markieren.';
export const X_MARK_INSTRUCTION_EN = 'Please mark whatever applies with an X.';

/** Obligation / voluntariness notice, verbatim. */
export const OBLIGATION_NOTICE =
  'Keine Pflicht zur Beantwortung; ohne die Angaben kann die Bewerbung ggf. nicht berücksichtigt werden';
export const OBLIGATION_NOTICE_EN =
  'You are not obliged to answer; without the information, the application may possibly not be considered';

/**
 * Truth declaration above the signature block (Spec §2.5, reference-form
 * derived, legally softened wording – lawyer-gate item §11.5).
 */
export const TRUTH_DECLARATION =
  'Ich versichere / Wir versichern, dass die vorstehenden Angaben nach bestem Wissen vollständig und richtig sind. Bewusst falsche Angaben können den Vermieter zur Kündigung des Mietverhältnisses berechtigen.';
export const TRUTH_DECLARATION_EN =
  'I/We affirm that the above information is complete and correct to the best of my/our knowledge. Deliberately false statements may entitle the landlord to terminate the tenancy.';

const DOCUMENT_TITLE: LocalizedText = {
  de: 'Mieterselbstauskunft',
  en: 'Tenant Self-Disclosure',
};
const SIGNATURE_PLACE_LABEL: LocalizedText = {
  de: 'Ort, Datum',
  en: 'Place, date',
};

/** Deterministic default timestamp when the caller passes no `now`. */
const DETERMINISTIC_EPOCH = new Date(0);

// ── Page geometry (Spec §2.5, normative) ──────────────────────────────────────
// Seit PR-2 (Spec 2026-07-30-legal-pdf-renderer) in `@kit/pdf-layout`. Hier
// re-exportiert, damit Bestandsimporte (u. a. `__tests__/layout-engine.test.ts`)
// unverändert bleiben.

export {
  PAGE_WIDTH,
  PAGE_HEIGHT,
  MARGIN,
  CONTENT_WIDTH,
  USABLE_PAGE_HEIGHT,
} from '@kit/pdf-layout';

/** Label column width (Spec §2.5 widget layout table). */
export const LABEL_COL = 190;
/** Cell padding on every side of a table cell. */
export const CELL_PAD = 4;
/** Options/widget line height inside table cells. */
export const WIDGET_LINE_HEIGHT = 14;
/** Options line height for bilingual (`de-en`) two-line option labels. */
export const WIDGET_LINE_HEIGHT_BILINGUAL = 24;
/**
 * Single-line AcroForm text-field height inside table cells (Spec 2026-07-13
 * §2a/OQ3: 16 → 18, closer to the reference-form 20-pt rect; leaves the §2.5
 * height raster intact – layout-engine.test.ts imports this constant).
 */
export const TEXT_FIELD_HEIGHT = 18;
/** Full-width textarea AcroForm field height. */
export const TEXTAREA_FIELD_HEIGHT = 48;

/**
 * Long text fields that leave the cramped 4-column raster and render as a
 * full-width, single-line field per answer column (Spec 2026-07-13 §2a/OQ1
 * "Mittelweg A"). At 4 columns a 66-pt cell forces auto-size down to ~5 pt for
 * a real address – unreadable. These keys render OUTSIDE the person-column
 * table (after the section table, like the textarea blocks), one full-width
 * field per person/guarantor, so the short fields' column raster stays intact.
 * `contact_phone` is deliberately NOT here (short, numeric – §6-OQ5).
 */
export const FULL_WIDTH_TEXT_FIELD_KEYS: readonly string[] = [
  'contact_address',
  'contact_email',
  'employer',
  'occupation',
];

const FULL_WIDTH_TEXT_FIELD_KEY_SET = new Set(FULL_WIDTH_TEXT_FIELD_KEYS);

/**
 * Answer column width for a given number of answer columns (Spec §2.5). The
 * parameter is the total column count (persons + optional guarantor, §2) – NOT
 * merely the person count. Measurement AND drawing MUST pass the same count.
 */
export function answerColumnWidth(columnCount: number): number {
  return (CONTENT_WIDTH - LABEL_COL) / columnCount;
}

const TITLE_SIZE = 18;
const HEADING_SIZE = 11;
const BODY_SIZE = 9.5;
const SMALL_SIZE = 8;
const LINE_GAP = 4;
const LABEL_TO_FIELD_GAP = 3;

const TEXT_COLOR = rgb(0.12, 0.12, 0.12);
const MUTED_COLOR = rgb(0.4, 0.4, 0.4);
const WARN_COLOR = rgb(0.6, 0.1, 0.1);
const CELL_BORDER_COLOR = rgb(0.62, 0.62, 0.62);
const CELL_BORDER_WIDTH = 0.5;
/** Drawn signature line (Spec 2026-07-13 §2c) – dark grey, no enclosing frame. */
const SIGNATURE_LINE_COLOR = rgb(0.3, 0.3, 0.3);

// ── yesno / checkbox compact pair metrics (Spec §2.5, R3d; §2d X-fields) ──────

/**
 * Square "X-field" size (Spec 2026-07-13 §2d/OQ4): every former `/Btn`
 * (yes/no radios, checkboxes, checkbox_group) is now a compact `/Tx` X-field
 * the applicant types an "X" into – the ONLY widget type iOS QuickLook can
 * edit (§0.6). 11 pt is raster-conform inside `WIDGET_LINE_HEIGHT = 14`
 * (3 pt luft) and keeps the yes/no pair narrow enough that the stacked
 * fallback is not triggered permanently (14 pt would have collided).
 */
export const X_FIELD_SIZE = 11;
const YESNO_LABEL_GAP = 3;
const YESNO_PAIR_GAP = 8;

/**
 * Measured width of the compact horizontal yes/no X-field pair. The SAME
 * function drives the stacked-fallback decision (measurement) and the
 * drawing offsets – they cannot diverge (Spec §2.5 defensive rule).
 */
export function measureYesNoPairWidth(
  font: PDFFont,
  yesLabel: string,
  noLabel: string,
): number {
  return (
    X_FIELD_SIZE +
    YESNO_LABEL_GAP +
    font.widthOfTextAtSize(yesLabel, SMALL_SIZE) +
    YESNO_PAIR_GAP +
    X_FIELD_SIZE +
    YESNO_LABEL_GAP +
    font.widthOfTextAtSize(noLabel, SMALL_SIZE)
  );
}

export interface RenderSelfDisclosureOptions {
  /** Fixed timestamp for creation/modification metadata (determinism). */
  now?: Date;
}

// ── Language helpers ──────────────────────────────────────────────────────────

export type RenderLanguage = SelfDisclosureConfig['language'];

/** Localized display texts: one entry per rendered language line. */
function localized(text: LocalizedText, language: RenderLanguage): string[] {
  switch (language) {
    case 'de':
      return [text.de];
    case 'en':
      return [text.en];
    case 'de-en':
      return [text.de, text.en];
  }
}

function yesNoLabels(language: RenderLanguage): { yes: string; no: string } {
  switch (language) {
    case 'de':
      return { yes: 'Ja', no: 'Nein' };
    case 'en':
      return { yes: 'Yes', no: 'No' };
    case 'de-en':
      return { yes: 'Ja / Yes', no: 'Nein / No' };
  }
}

// ── Answer columns (persons + optional guarantor, §2) ─────────────────────────

/** AcroForm suffix source for one answer column. */
export type AnswerColumnKey = 'p1' | 'p2' | 'p3' | 'g';

export interface AnswerColumn {
  /** AcroForm suffix source: `_p{n}` for a person, `_g` for the guarantor. */
  key: AnswerColumnKey;
  /** 1-based person index; null = the guarantor column. */
  personIndex: number | null;
}

/**
 * The ordered answer columns of a form: one per person, then the optional
 * guarantor column (§2). The SINGLE derivation of the column model – every
 * consumer (width measurement, cell drawing, AcroForm names, column heads,
 * signature rows) iterates over this, so person count and guarantor can never
 * drift apart.
 */
export function answerColumns(
  config: Pick<SelfDisclosureConfig, 'personCount' | 'includeGuarantor'>,
): AnswerColumn[] {
  const columns: AnswerColumn[] = Array.from(
    { length: config.personCount },
    (_, i): AnswerColumn => ({
      key: `p${i + 1}` as AnswerColumnKey,
      personIndex: i + 1,
    }),
  );

  if (config.includeGuarantor) {
    columns.push({ key: 'g', personIndex: null });
  }

  return columns;
}

/** AcroForm suffix for one answer column (`_p{n}` / `_g`). */
function columnSuffix(column: AnswerColumn): string {
  return column.personIndex === null ? '_g' : `_p${column.personIndex}`;
}

// ── AcroForm names (R11) ──────────────────────────────────────────────────────

export function acroFieldName(
  field: Pick<SelfDisclosureField, 'key' | 'scope'>,
  column: AnswerColumn | null,
): string {
  return field.scope === 'household' || column === null
    ? field.key
    : `${field.key}${columnSuffix(column)}`;
}

export function acroOptionFieldName(
  field: Pick<SelfDisclosureField, 'key' | 'scope'>,
  optionKey: string,
  column: AnswerColumn | null,
): string {
  const base = `${field.key}__${optionKey}`;

  return field.scope === 'household' || column === null
    ? base
    : `${base}${columnSuffix(column)}`;
}

// ── Collectible render content (pure – used by renderer + unit tests) ─────────

export interface RenderFieldEntry {
  field: SelfDisclosureField;
  /** Per-language display labels (threshold amount applied). */
  labelTexts: string[];
  /** Per-language PDF hints (empty when the field has none). */
  hintTexts: string[];
  /** checkbox_group options: per-language labels each. */
  optionTexts?: { key: string; texts: string[] }[];
}

export interface RenderSection {
  key: FieldSection;
  headingTexts: string[];
  fields: RenderFieldEntry[];
}

export interface SelfDisclosureRenderContent {
  language: RenderLanguage;
  personCount: number;
  /** Whether the form carries the optional guarantor column (§2). */
  includeGuarantor: boolean;
  /** Persons + optional guarantor – the ordered answer columns (§2). */
  answerColumns: AnswerColumn[];
  titleTexts: string[];
  headerLines: string[];
  obligationTexts: string[];
  warningTexts: string[];
  /** X-field usage instruction ("Zutreffendes mit X markieren", §2d). */
  xInstructionTexts: string[];
  /** Per answer column: head texts (person/guarantor designation, per language). */
  columnHeadTexts: string[][];
  sections: RenderSection[];
  art13Layers: Art13LanguageLayer[];
  truthTexts: string[];
  signPlaceDateTexts: string[];
  /** Per answer column: signature-line labels (per language). */
  signatureLabelTexts: string[][];
  disclaimerTexts: string[];
  /** Every visible text string that will be drawn (NIE scan + assertions). */
  textBlocks: string[];
}

function senderHeaderLines(config: SelfDisclosureConfig): string[] {
  const { sender } = config;
  const lines: string[] = [];

  if (sender.propertyLabel) {
    lines.push(sender.propertyLabel);
  }

  const responsible = sender.contactPerson
    ? `${sender.displayName} (${sender.contactPerson})`
    : sender.displayName;

  lines.push(
    config.language === 'en'
      ? `Provider/Landlord: ${responsible}`
      : `Anbieter/Vermieter: ${responsible}`,
  );

  const address = [
    sender.street,
    [sender.postalCode, sender.city].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  if (address) lines.push(address);
  if (sender.email) lines.push(sender.email);

  return lines;
}

function fieldDisplayTexts(
  field: SelfDisclosureField,
  config: SelfDisclosureConfig,
): string[] {
  if (
    field.incomeVariant === 'threshold' &&
    config.fieldOptions.incomeThresholdEur !== undefined
  ) {
    const threshold = config.fieldOptions.incomeThresholdEur;

    return localized(
      {
        de: `${field.label.de} von mindestens ${threshold} Euro`,
        en: `${field.label.en} of at least ${threshold} euros`,
      },
      config.language,
    );
  }

  return localized(field.label, config.language);
}

function columnHeadText(column: AnswerColumn): LocalizedText {
  if (column.personIndex === null) {
    return { de: 'Bürge', en: 'Guarantor' };
  }

  return column.personIndex === 1
    ? { de: 'Mietbewerber', en: 'Tenant applicant' }
    : {
        de: `Weitere Person ${column.personIndex}`,
        en: `Additional person ${column.personIndex}`,
      };
}

function signatureLabelText(column: AnswerColumn): LocalizedText {
  if (column.personIndex === null) {
    return { de: 'Unterschrift Bürge', en: 'Signature guarantor' };
  }

  return column.personIndex === 1
    ? { de: 'Unterschrift Mietbewerber', en: 'Signature tenant applicant' }
    : {
        de: `Unterschrift weitere Person ${column.personIndex}`,
        en: `Signature additional person ${column.personIndex}`,
      };
}

/**
 * Builds the full text/field content the renderer draws. Pure and
 * deterministic; the NIE-list unit test asserts against `textBlocks`.
 */
export function buildRenderContent(
  config: SelfDisclosureConfig,
  _opts: RenderSelfDisclosureOptions = {},
): SelfDisclosureRenderContent {
  const { language, personCount } = config;
  const columns = answerColumns(config);

  const enabled = new Set(config.enabledFields);
  const sections: RenderSection[] = [];

  for (const sectionKey of FIELD_SECTION_ORDER) {
    const fields: RenderFieldEntry[] = [];

    for (const key of config.enabledFields) {
      const field = getFieldByKey(key);

      if (!field || field.section !== sectionKey) continue;
      if (!enabled.has(key)) continue; // defensive

      fields.push({
        field,
        labelTexts: fieldDisplayTexts(field, config),
        hintTexts: field.hint ? localized(field.hint, language) : [],
        optionTexts: field.options?.map((option) => ({
          key: option.key,
          texts: localized(option.label, language),
        })),
      });
    }

    if (fields.length > 0) {
      sections.push({
        key: sectionKey,
        headingTexts: localized(FIELD_SECTION_LABELS[sectionKey], language),
        fields,
      });
    }
  }

  const columnHeadTexts = columns.map((column) =>
    localized(columnHeadText(column), language),
  );
  const signatureLabelTexts = columns.map((column) =>
    localized(signatureLabelText(column), language),
  );

  const content: SelfDisclosureRenderContent = {
    language,
    personCount,
    includeGuarantor: config.includeGuarantor,
    answerColumns: columns,
    titleTexts: localized(DOCUMENT_TITLE, language),
    headerLines: senderHeaderLines(config),
    obligationTexts: localized(
      { de: OBLIGATION_NOTICE, en: OBLIGATION_NOTICE_EN },
      language,
    ),
    warningTexts: localized(
      { de: V2_WARNING_NOTICE, en: V2_WARNING_NOTICE_EN },
      language,
    ),
    xInstructionTexts: localized(
      { de: X_MARK_INSTRUCTION, en: X_MARK_INSTRUCTION_EN },
      language,
    ),
    columnHeadTexts,
    sections,
    art13Layers: buildArt13Layers(config),
    truthTexts: localized(
      { de: TRUTH_DECLARATION, en: TRUTH_DECLARATION_EN },
      language,
    ),
    signPlaceDateTexts: localized(SIGNATURE_PLACE_LABEL, language),
    signatureLabelTexts,
    disclaimerTexts: localized(
      { de: DISCLAIMER_FOOTER, en: DISCLAIMER_FOOTER_EN },
      language,
    ),
    textBlocks: [],
  };

  content.textBlocks = collectTextBlocks(content);

  return content;
}

function collectTextBlocks(content: SelfDisclosureRenderContent): string[] {
  const blocks: string[] = [...content.titleTexts];

  blocks.push(...content.headerLines);
  blocks.push(...content.obligationTexts, ...content.warningTexts);
  blocks.push(...content.xInstructionTexts);

  for (const column of content.columnHeadTexts) blocks.push(...column);

  for (const section of content.sections) {
    blocks.push(...section.headingTexts);

    for (const entry of section.fields) {
      blocks.push(...entry.labelTexts);
      blocks.push(...entry.hintTexts);

      for (const option of entry.optionTexts ?? []) {
        blocks.push(...option.texts);
      }
    }
  }

  for (const layer of content.art13Layers) {
    // Die Überschrift der Kurz-Schicht gehört in die Zeichenabdeckung wie jeder
    // andere Text — sonst fehlt ihr Zeichensatz genau dann, wenn sie gedruckt
    // wird.
    blocks.push(layer.shortHeading);
    blocks.push(...layer.shortLines);
    blocks.push(...layer.fullLines);
  }

  blocks.push(...content.truthTexts);
  blocks.push(...content.signPlaceDateTexts);

  for (const labels of content.signatureLabelTexts) blocks.push(...labels);

  blocks.push(...content.disclaimerTexts);

  return blocks.filter((line) => line.trim().length > 0);
}

// ── Text wrapping (shared by measure AND draw – one source of truth) ──────────

// `longestFittingPrefix` und `wrapText` liegen seit PR-2 in `@kit/pdf-layout`
// (oben importiert) – identische Implementierung, nur verschoben.

// ── Two-phase layout engine (PR 2a, extended for table blocks in 2b) ──────────
//
// Every drawable unit is a LayoutBlock composed of primitive rows. The SAME
// row list drives measurement (sum of row heights) and drawing – one
// measuring function for measuring AND drawing, so the `layoutDocument()`
// test harness cannot diverge from the real renderer.
//
// Placement rules (placeLayoutItems, pure + deterministic):
//  - keep-together: a block that does not fit the remaining page space breaks
//    the page BEFORE the block;
//  - repeatHeader (2b): table blocks carry the column-head row; when a page
//    break happens before such a block, the head row is re-drawn on top of
//    the new page first (Spaltenkopf-Wiederholung, §2.5);
//  - orphan rule: `splittable` paragraph blocks may split at row boundaries
//    only if ≥ 3 rows stay on the current page;
//  - degenerate guard: a block taller than one usable page falls back to
//    row-wise flow – controlled, no endless loop, `console.warn` logged.

// Orphan-Regel, Text-Primitive und die Platzierungs-Engine liegen seit PR-2 in
// `@kit/pdf-layout`. Re-Export, damit Bestandsimporte unverändert bleiben.
export type { FontStyle, CellLine, LineStyle } from '@kit/pdf-layout';

export interface TableOptionRow {
  optionKey: string;
  lines: CellLine[];
  rowHeight: number;
}

export type LayoutRow =
  | ({ type: 'text' } & CellLine)
  | { type: 'gap'; height: number }
  | {
      type: 'textFieldRow';
      fieldName: string;
      multiline: boolean;
      fieldHeight: number;
      height: number;
    }
  | {
      type: 'sectionHeadingRow';
      lines: CellLine[];
      height: number;
    }
  | {
      type: 'tableHeadRow';
      /** Total answer columns (persons + optional guarantor, §2). */
      answerColumnCount: number;
      columns: CellLine[][];
      height: number;
    }
  | {
      type: 'tableFieldRow';
      fieldKey: string;
      scope: SelfDisclosureField['scope'];
      kind: Exclude<SelfDisclosureField['kind'], 'textarea'>;
      /** Total answer columns (persons + optional guarantor, §2). */
      answerColumnCount: number;
      /** The ordered answer columns – drives cell placement + AcroForm names. */
      answerColumns: AnswerColumn[];
      labelLines: CellLine[];
      optionRows?: TableOptionRow[];
      yesnoStacked?: boolean;
      yesLabel?: string;
      noLabel?: string;
      /** Measured components (test harness asserts the §2.5 height formula). */
      labelStackHeight: number;
      widgetHeight: number;
      height: number;
    }
  | {
      type: 'personSignatureRow';
      fieldName: string;
      placeDateLines: CellLine[];
      signatureLines: CellLine[];
      height: number;
    };

/**
 * Container-Typen kommen seit PR-2 generisch aus `@kit/pdf-layout`; hier auf die
 * fachliche `LayoutRow`-Union festgezurrt, damit alle Bestandsverwendungen
 * (u. a. `const block: LayoutBlock = …` in `layout-engine.test.ts`) unverändert
 * gültig bleiben.
 */
export type LayoutBlock = PdfLayoutBlock<LayoutRow>;
export type LayoutSpacer = { spacer: number };
export type LayoutItem = PdfLayoutItem<LayoutRow>;
export type PlacedBlockFragment = PdfPlacedBlockFragment<LayoutRow>;
export type PlacedLayout = PdfPlacedLayout<LayoutRow>;

/**
 * Platzierung für die Selbstauskunft. Dünner Wrapper um die generische Engine —
 * fixiert den Row-Typ und behält das bisherige Warn-Label bei, damit sich die
 * Degenerate-Logzeile nicht ändert.
 */
export function placeLayoutItems(items: readonly LayoutItem[]): PlacedLayout {
  return placeLayoutItemsGeneric<LayoutRow>(items, {
    label: 'self-disclosure',
  });
}

// Die Platzierungs-Engine selbst liegt seit PR-2 in `@kit/pdf-layout`;
// der Wrapper oben fixiert nur Row-Typ und Warn-Label.

// ── Block construction (v2 table layout as blocks) ────────────────────────────

/**
 * Style-Defaults der Selbstauskunft. Bis PR-2 las `styledLines` diese Konstanten
 * direkt aus dem Modul; die generische Fassung in `@kit/pdf-layout` bekommt sie
 * jetzt explizit übergeben (Review-Finding F8) — Werte unverändert.
 */
const STYLE_DEFAULTS: StyleDefaults = {
  size: BODY_SIZE,
  color: TEXT_COLOR,
  lineGap: LINE_GAP,
};

/** Dünner Wrapper, damit alle Aufrufstellen ihre bisherige Signatur behalten. */
function styledLines(
  text: string,
  fonts: Fonts,
  maxWidth: number,
  style: LineStyle = {},
): CellLine[] {
  return styledLinesGeneric(text, fonts, maxWidth, STYLE_DEFAULTS, style);
}

/**
 * Localized text stack: primary language line(s) first, secondary (EN in
 * `de-en`) italic/smaller below – Spec §2.5 bilingual decision.
 */
function localizedStack(
  texts: readonly string[],
  fonts: Fonts,
  maxWidth: number,
  primary: LineStyle = {},
): CellLine[] {
  const lines: CellLine[] = [];

  texts.forEach((text, index) => {
    if (index === 0) {
      lines.push(...styledLines(text, fonts, maxWidth, primary));
    } else {
      lines.push(
        ...styledLines(text, fonts, maxWidth, {
          size: SMALL_SIZE,
          font: 'italic',
          color: primary.color ?? MUTED_COLOR,
        }),
      );
    }
  });

  return lines;
}

function paragraphRows(
  text: string,
  fonts: Fonts,
  style: LineStyle = {},
): LayoutRow[] {
  const size = style.size ?? BODY_SIZE;

  if (text.trim().length === 0) {
    return [{ type: 'gap', height: size * 0.6 }];
  }

  return styledLines(text, fonts, CONTENT_WIDTH, style).map((line) => ({
    type: 'text',
    ...line,
  }));
}

function localizedParagraphRows(
  texts: readonly string[],
  fonts: Fonts,
  primary: LineStyle = {},
): LayoutRow[] {
  return localizedStack(texts, fonts, CONTENT_WIDTH, primary).map((line) => ({
    type: 'text',
    ...line,
  }));
}

const LABEL_INNER_WIDTH = LABEL_COL - 2 * CELL_PAD;

/** Column-head row (person designations), bilingual two-line. */
function buildTableHeadRow(
  content: SelfDisclosureRenderContent,
  fonts: Fonts,
): Extract<LayoutRow, { type: 'tableHeadRow' }> {
  const colWidth =
    answerColumnWidth(content.answerColumns.length) - 2 * CELL_PAD;

  const columns = content.columnHeadTexts.map((texts) =>
    localizedStack(texts, fonts, colWidth, { font: 'bold' }),
  );

  const maxStack = Math.max(
    ...columns.map((lines) => lines.reduce((sum, l) => sum + l.height, 0)),
  );

  return {
    type: 'tableHeadRow',
    answerColumnCount: content.answerColumns.length,
    columns,
    height: maxStack + 2 * CELL_PAD,
  };
}

function buildSectionHeadingRow(
  headingTexts: readonly string[],
  fonts: Fonts,
): Extract<LayoutRow, { type: 'sectionHeadingRow' }> {
  const lines = localizedStack(headingTexts, fonts, CONTENT_WIDTH, {
    size: HEADING_SIZE,
    font: 'bold',
  });

  return {
    type: 'sectionHeadingRow',
    lines,
    height: lines.reduce((sum, line) => sum + line.height, 0) + 6,
  };
}

/**
 * Builds one table row for a non-textarea field, per the normative widget
 * layout table (Spec §2.5): row height = max(label stack, widget height) +
 * 2×CELL_PAD – except checkbox_group, where the options band sits BELOW the
 * field label in the label column (options labels once in the label column,
 * answer cells only checkboxes, line-aligned), so the row height is
 * labelStack + options band + 2×CELL_PAD.
 */
function buildTableFieldRow(
  entry: RenderFieldEntry,
  content: SelfDisclosureRenderContent,
  fonts: Fonts,
): Extract<LayoutRow, { type: 'tableFieldRow' }> {
  const { field } = entry;
  const { language } = content;
  const answerColumnCount = content.answerColumns.length;

  if (field.kind === 'textarea') {
    throw new Error(
      `textarea field "${field.key}" must render as a full-width block, not a table row`,
    );
  }

  const labelLines: CellLine[] = localizedStack(
    entry.labelTexts,
    fonts,
    LABEL_INNER_WIDTH,
  );

  // PDF hints render ALWAYS in the label column under the label (R3e).
  entry.hintTexts.forEach((hint, index) => {
    labelLines.push(
      ...styledLines(hint, fonts, LABEL_INNER_WIDTH, {
        size: SMALL_SIZE,
        font: index === 0 ? 'regular' : 'italic',
        color: MUTED_COLOR,
      }),
    );
  });

  const labelStackHeight = labelLines.reduce((sum, l) => sum + l.height, 0);

  // F1: the measurement MUST use the same total column count the draw phase
  // places (persons + guarantor) – otherwise the yesnoStacked decision below
  // diverges from the rendered cell width (de-en × 2P + guarantor kip case).
  const cellInnerWidth =
    (field.scope === 'household'
      ? CONTENT_WIDTH - LABEL_COL
      : answerColumnWidth(answerColumnCount)) -
    2 * CELL_PAD;

  const labels = yesNoLabels(language);
  const optionRowHeight =
    language === 'de-en' ? WIDGET_LINE_HEIGHT_BILINGUAL : WIDGET_LINE_HEIGHT;

  let widgetHeight: number;
  let yesnoStacked: boolean | undefined;
  let optionRows: TableOptionRow[] | undefined;

  switch (field.kind) {
    case 'text':
    case 'number':
    case 'date':
      widgetHeight = TEXT_FIELD_HEIGHT;
      break;
    case 'checkbox':
      widgetHeight = WIDGET_LINE_HEIGHT;
      break;
    case 'yesno': {
      const pairWidth = measureYesNoPairWidth(
        fonts.regular,
        labels.yes,
        labels.no,
      );

      yesnoStacked = pairWidth > cellInnerWidth;
      widgetHeight = yesnoStacked ? 2 * WIDGET_LINE_HEIGHT : WIDGET_LINE_HEIGHT;
      break;
    }
    case 'checkbox_group': {
      // Option labels draw with an 8 pt indent inside the label column – the
      // wrap width accounts for it so lines never cross the cell border.
      optionRows = (entry.optionTexts ?? []).map((option) => ({
        optionKey: option.key,
        lines: localizedStack(option.texts, fonts, LABEL_INNER_WIDTH - 8),
        rowHeight: optionRowHeight,
      }));
      widgetHeight = optionRows.length * optionRowHeight;
      break;
    }
  }

  const height =
    field.kind === 'checkbox_group'
      ? labelStackHeight + widgetHeight + 2 * CELL_PAD
      : Math.max(labelStackHeight, widgetHeight) + 2 * CELL_PAD;

  return {
    type: 'tableFieldRow',
    fieldKey: field.key,
    scope: field.scope,
    kind: field.kind,
    answerColumnCount,
    answerColumns: content.answerColumns,
    labelLines,
    optionRows,
    yesnoStacked,
    yesLabel: labels.yes,
    noLabel: labels.no,
    labelStackHeight,
    widgetHeight,
    height,
  };
}

function buildSignatureRow(
  column: AnswerColumn,
  columnIndex: number,
  content: SelfDisclosureRenderContent,
  fonts: Fonts,
): Extract<LayoutRow, { type: 'personSignatureRow' }> {
  const placeDateLines = localizedStack(
    content.signPlaceDateTexts,
    fonts,
    220,
    { size: SMALL_SIZE, color: MUTED_COLOR },
  );
  const signatureLines = localizedStack(
    content.signatureLabelTexts[columnIndex] ?? [],
    fonts,
    CONTENT_WIDTH - 260,
    { size: SMALL_SIZE, color: MUTED_COLOR },
  );

  const labelsHeight = Math.max(
    placeDateLines.reduce((sum, l) => sum + l.height, 0),
    signatureLines.reduce((sum, l) => sum + l.height, 0),
  );

  return {
    type: 'personSignatureRow',
    fieldName: `sign_place_date${columnSuffix(column)}`,
    placeDateLines,
    signatureLines,
    height: TEXT_FIELD_HEIGHT + labelsHeight + 10,
  };
}

/**
 * Full-width per-column blocks (label + hints + one AcroForm text field spanning
 * the whole content width). Shared by the multiline textarea (§2.5 exception)
 * and the single-line long-field path (Spec 2026-07-13 §2a/OQ1). One block per
 * answer column – persons + optional guarantor (§2).
 */
function buildFullWidthFieldBlocks(
  entry: RenderFieldEntry,
  content: SelfDisclosureRenderContent,
  fonts: Fonts,
  variant: { idPrefix: string; multiline: boolean; fieldHeight: number },
): LayoutBlock[] {
  const blocks: LayoutBlock[] = [];

  // A per-column suffix distinguishes the blocks whenever more than one column
  // exists – i.e. multiple persons OR the guarantor column (§2).
  const showSuffix = content.personCount > 1 || content.includeGuarantor;

  for (const column of content.answerColumns) {
    const rows: LayoutRow[] = [];
    const columnSuffixText: LocalizedText | null = showSuffix
      ? column.personIndex === null
        ? { de: ' – Bürge', en: ' – guarantor' }
        : {
            de: ` – Person ${column.personIndex}`,
            en: ` – person ${column.personIndex}`,
          }
      : null;

    const suffixLines = columnSuffixText
      ? localized(columnSuffixText, content.language)
      : null;

    const labelTexts = entry.labelTexts.map((text, index) =>
      suffixLines ? `${text}${suffixLines[index] ?? ''}` : text,
    );

    rows.push(
      ...localizedStack(labelTexts, fonts, CONTENT_WIDTH).map(
        (line): LayoutRow => ({ type: 'text', ...line }),
      ),
    );

    entry.hintTexts.forEach((hint, index) => {
      rows.push(
        ...styledLines(hint, fonts, CONTENT_WIDTH, {
          size: SMALL_SIZE,
          font: index === 0 ? 'regular' : 'italic',
          color: MUTED_COLOR,
        }).map((line): LayoutRow => ({ type: 'text', ...line })),
      );
    });

    rows.push({
      type: 'textFieldRow',
      fieldName: `${entry.field.key}${columnSuffix(column)}`,
      multiline: variant.multiline,
      fieldHeight: variant.fieldHeight,
      height: variant.fieldHeight + LABEL_TO_FIELD_GAP,
    });
    rows.push({ type: 'gap', height: 6 });

    blocks.push({
      id: `${variant.idPrefix}:${entry.field.key}:${column.key}`,
      rows,
    });
  }

  return blocks;
}

/** Full-width textarea block per person (§2.5 exception). */
function buildTextareaBlocks(
  entry: RenderFieldEntry,
  content: SelfDisclosureRenderContent,
  fonts: Fonts,
): LayoutBlock[] {
  return buildFullWidthFieldBlocks(entry, content, fonts, {
    idPrefix: 'field',
    multiline: true,
    fieldHeight: TEXTAREA_FIELD_HEIGHT,
  });
}

/**
 * Full-width single-line block per person for a long text field
 * (Spec 2026-07-13 §2a/OQ1). Same wiring as the textarea but `multiline:false`
 * and the compact `TEXT_FIELD_HEIGHT`, plus a distinct `longfield:` block-ID
 * scheme so it never collides with the single table-row id `field:<key>`. The
 * AcroForm name stays `${key}_p{n}` / `${key}_g` – identical to the previous
 * in-table rendering, so no field name changes (acroform.test.ts unaffected).
 */
function buildLongFieldBlocks(
  entry: RenderFieldEntry,
  content: SelfDisclosureRenderContent,
  fonts: Fonts,
): LayoutBlock[] {
  return buildFullWidthFieldBlocks(entry, content, fonts, {
    idPrefix: 'longfield',
    multiline: false,
    fieldHeight: TEXT_FIELD_HEIGHT,
  });
}

/**
 * Builds the full v2 document as layout items. Block cut per §2.5: table
 * rows unteilbar; section headings keep-with-next (merged with the first row
 * of their table); Art.-13 short layers one block each; the WHOLE signature
 * block (truth declaration + all person rows) one block; Art.-13 full layers
 * on their own appendix pages; plain paragraphs splittable under the orphan
 * rule; column-head row repeats after page breaks via `repeatHeader`.
 */
function buildLayoutItems(
  content: SelfDisclosureRenderContent,
  fonts: Fonts,
): LayoutItem[] {
  const items: LayoutItem[] = [];

  items.push({
    id: 'title',
    rows: localizedParagraphRows(content.titleTexts, fonts, {
      size: TITLE_SIZE,
      font: 'bold',
    }),
  });
  items.push({ spacer: 4 });

  content.headerLines.forEach((line, index) => {
    items.push({
      id: `header:${index}`,
      rows: paragraphRows(line, fonts, { color: MUTED_COLOR }),
      splittable: true,
    });
  });

  items.push({ spacer: 6 });

  items.push({
    id: 'notice:obligation',
    rows: localizedParagraphRows(content.obligationTexts, fonts),
    splittable: true,
  });
  items.push({
    id: 'notice:warning',
    rows: localizedParagraphRows(content.warningTexts, fonts, {
      font: 'bold',
      color: WARN_COLOR,
    }),
    splittable: true,
  });
  items.push({
    id: 'notice:x-instruction',
    rows: localizedParagraphRows(content.xInstructionTexts, fonts, {
      size: SMALL_SIZE,
      color: MUTED_COLOR,
    }),
    splittable: true,
  });
  items.push({ spacer: 8 });

  // ── Table (sections) ────────────────────────────────────────────────────────

  const headRow = buildTableHeadRow(content, fonts);
  const repeatHeader = { id: 'table-head', rows: [headRow as LayoutRow] };

  for (const section of content.sections) {
    const headingRow = buildSectionHeadingRow(section.headingTexts, fonts);

    // Long text fields (Spec 2026-07-13 §2a/OQ1) leave the person-column table
    // and render full-width single-line AFTER it – like the textarea blocks,
    // outside the 4-column raster, so the short fields' columns stay compact.
    const tableEntries = section.fields.filter(
      (entry) =>
        entry.field.kind !== 'textarea' &&
        !FULL_WIDTH_TEXT_FIELD_KEY_SET.has(entry.field.key),
    );
    const longFieldEntries = section.fields.filter((entry) =>
      FULL_WIDTH_TEXT_FIELD_KEY_SET.has(entry.field.key),
    );
    const textareaEntries = section.fields.filter(
      (entry) => entry.field.kind === 'textarea',
    );

    // Every table block carries the repeatHeader – the engine places the
    // column head once per page ahead of the first table block on it (table
    // start included), so the head can never be missing above table rows.
    const sectionBlocks: LayoutBlock[] = tableEntries.map((entry) => {
      const row = buildTableFieldRow(entry, content, fonts);

      return { id: `field:${entry.field.key}`, rows: [row], repeatHeader };
    });

    if (sectionBlocks.length > 0) {
      const first = sectionBlocks[0];

      if (first) {
        // Keep-with-next: heading merged with the first row of its table.
        sectionBlocks[0] = {
          id: `section:${section.key}+${first.id}`,
          rows: [headingRow, ...first.rows],
          repeatHeader,
        };
      }

      items.push(...sectionBlocks);
    }

    // Full-width blocks follow the section table (reading order per §2a/OQ1):
    // long single-line fields first, then the textarea. `headingConsumed`
    // guards the degenerate case of a section made up ONLY of full-width
    // fields (no table row to carry the heading) – the heading then keeps
    // with the first full-width block instead.
    let headingConsumed = sectionBlocks.length > 0;

    for (const entry of longFieldEntries) {
      const longBlocks = buildLongFieldBlocks(entry, content, fonts);

      if (!headingConsumed && longBlocks[0]) {
        longBlocks[0] = {
          ...longBlocks[0],
          id: `section:${section.key}+${longBlocks[0].id}`,
          rows: [headingRow, ...longBlocks[0].rows],
        };
        headingConsumed = true;
      }

      items.push(...longBlocks);
    }

    for (const entry of textareaEntries) {
      const textareaBlocks = buildTextareaBlocks(entry, content, fonts);

      if (!headingConsumed && textareaBlocks[0]) {
        // Section with only textarea fields: heading keep-with-next with the
        // first textarea block instead (no column head – full-width blocks).
        textareaBlocks[0] = {
          ...textareaBlocks[0],
          id: `section:${section.key}+${textareaBlocks[0].id}`,
          rows: [headingRow, ...textareaBlocks[0].rows],
        };
        headingConsumed = true;
      }

      items.push(...textareaBlocks);
    }
  }

  items.push({ spacer: 6 });

  // Art.-13 short layer(s) – kept together as ONE block each (§2.5).
  content.art13Layers.forEach((layer, index) => {
    if (index > 0) items.push({ spacer: 4 });

    /*
     * 🔴 Die Überschrift steht IM SELBEN Block wie die Zeilen, nicht darüber.
     * Ein eigener Block dürfte umbrechen — und eine Überschrift allein am
     * Seitenfuß ist schlimmer als keine. Dieselbe Bindung, die die
     * Voll-Schicht unten über `keep-with-next` herstellt, ergibt sich hier
     * dadurch, dass §2.5 die Kurz-Schicht ohnehin als EINEN Block hält.
     */
    items.push({
      id: `art13-short:${layer.language}`,
      rows: [
        ...paragraphRows(layer.shortHeading, fonts, {
          size: SMALL_SIZE,
          font: 'bold',
        }),
        ...layer.shortLines.flatMap((line) =>
          paragraphRows(line, fonts, { size: SMALL_SIZE, color: MUTED_COLOR }),
        ),
      ],
    });
  });

  items.push({ spacer: 10 });

  // Signature block: truth declaration + ALL person rows, ONE block (§2.5).
  const signatureRows: LayoutRow[] = localizedParagraphRows(
    content.truthTexts,
    fonts,
    { font: 'bold' },
  );

  signatureRows.push({ type: 'gap', height: 8 });

  // One signature row per answer column – persons, then the guarantor (§2).
  content.answerColumns.forEach((column, index) => {
    signatureRows.push(buildSignatureRow(column, index, content, fonts));

    if (index < content.answerColumns.length - 1) {
      signatureRows.push({ type: 'gap', height: 8 });
    }
  });

  items.push({ id: 'signature', rows: signatureRows });

  // Art.-13 full layer(s) – own appendix page(s). Heading keep-with-next:
  // merged with the first following non-empty line.
  for (const layer of content.art13Layers) {
    const fullLines = layer.fullLines;
    const headingRows: LayoutRow[] = paragraphRows(fullLines[0] ?? '', fonts, {
      size: HEADING_SIZE,
      font: 'bold',
    });

    let index = 1;

    while (index < fullLines.length) {
      const line = fullLines[index] ?? '';

      headingRows.push(
        ...paragraphRows(line, fonts, { size: SMALL_SIZE, color: MUTED_COLOR }),
      );
      index += 1;

      if (line.trim().length > 0) break;
    }

    items.push({
      id: `art13-full:${layer.language}:heading`,
      rows: headingRows,
      pageBreakBefore: true,
    });

    for (; index < fullLines.length; index += 1) {
      items.push({
        id: `art13-full:${layer.language}:${index}`,
        rows: paragraphRows(fullLines[index] ?? '', fonts, {
          size: SMALL_SIZE,
          color: MUTED_COLOR,
        }),
        splittable: true,
      });
    }
  }

  items.push({ spacer: 12 });

  // Disclaimer footer – EXACTLY ONCE per language.
  items.push({
    id: 'disclaimer',
    rows: content.disclaimerTexts.flatMap((line) =>
      paragraphRows(line, fonts, { size: SMALL_SIZE, color: MUTED_COLOR }),
    ),
    splittable: true,
  });

  return items;
}

// ── Draw phase (interprets the SAME rows the measurement summed up) ───────────

interface AnswerCell {
  x: number;
  width: number;
  /** The answer column; null = merged household cell (no per-column suffix). */
  column: AnswerColumn | null;
}

function answerCells(
  scope: SelfDisclosureField['scope'],
  columns: readonly AnswerColumn[],
): AnswerCell[] {
  if (scope === 'household') {
    return [
      {
        x: MARGIN + LABEL_COL,
        width: CONTENT_WIDTH - LABEL_COL,
        column: null,
      },
    ];
  }

  const colWidth = answerColumnWidth(columns.length);

  return columns.map((column, i) => ({
    x: MARGIN + LABEL_COL + i * colWidth,
    width: colWidth,
    column,
  }));
}

function drawCellBorder(
  page: PDFPage,
  x: number,
  topY: number,
  width: number,
  height: number,
): void {
  page.drawRectangle({
    x,
    y: topY - height,
    width,
    height,
    borderColor: CELL_BORDER_COLOR,
    borderWidth: CELL_BORDER_WIDTH,
  });
}

function drawCellLines(
  lines: readonly CellLine[],
  page: PDFPage,
  fonts: Fonts,
  x: number,
  topY: number,
): number {
  let y = topY;

  for (const line of lines) {
    page.drawText(line.text, {
      x,
      y: y - line.size,
      size: line.size,
      font: fontFor(fonts, line.font),
      color: line.color,
    });
    y -= line.height;
  }

  return y;
}

/**
 * Creates ONE square `/Tx` "X-field" (Spec 2026-07-13 §2d) at the given
 * top-left band position. `/Tx` is the only widget type iOS QuickLook can edit
 * (§0.6), so every former `/Btn` becomes one of these. The visible box comes
 * from an explicit `/MK` (white BG, black border colour) + `/BS` (1 pt) on the
 * widget dict – with `updateFieldAppearances:false` (§2a) pdf-lib bakes no
 * appearance, so without this the box would be invisible AND the print/tick
 * fallback impossible. Auto-size via `setFontSize(0)`; no `/MaxLen`.
 * Deterministic: constant geometry, stable dict order.
 */
function createXField(
  form: PDFForm,
  page: PDFPage,
  fieldName: string,
  x: number,
  y: number,
): void {
  const textField = form.createTextField(fieldName);

  textField.addToPage(page, {
    x,
    y,
    width: X_FIELD_SIZE,
    height: X_FIELD_SIZE,
  });
  textField.setFontSize(0);

  const widgetDict = textField.acroField.getWidgets()[0]?.dict;

  if (widgetDict) {
    const ctx = widgetDict.context;

    widgetDict.set(
      PDFName.of('MK'),
      ctx.obj({ BG: ctx.obj([1, 1, 1]), BC: ctx.obj([0, 0, 0]) }),
    );
    widgetDict.set(
      PDFName.of('BS'),
      ctx.obj({ W: PDFNumber.of(1), S: PDFName.of('S') }),
    );
  }
}

function drawYesNo(
  page: PDFPage,
  form: PDFForm,
  fonts: Fonts,
  yesName: string,
  noName: string,
  x: number,
  bandTop: number,
  stacked: boolean,
  yesLabel: string,
  noLabel: string,
): void {
  const drawOption = (
    fieldName: string,
    label: string,
    optionX: number,
    optionBandTop: number,
  ) => {
    createXField(
      form,
      page,
      fieldName,
      optionX,
      optionBandTop - WIDGET_LINE_HEIGHT + 1.5,
    );
    page.drawText(label, {
      x: optionX + X_FIELD_SIZE + YESNO_LABEL_GAP,
      y: optionBandTop - WIDGET_LINE_HEIGHT + 3.5,
      size: SMALL_SIZE,
      font: fonts.regular,
      color: TEXT_COLOR,
    });
  };

  if (stacked) {
    drawOption(yesName, yesLabel, x, bandTop);
    drawOption(noName, noLabel, x, bandTop - WIDGET_LINE_HEIGHT);

    return;
  }

  drawOption(yesName, yesLabel, x, bandTop);

  const noX =
    x +
    X_FIELD_SIZE +
    YESNO_LABEL_GAP +
    fonts.regular.widthOfTextAtSize(yesLabel, SMALL_SIZE) +
    YESNO_PAIR_GAP;

  drawOption(noName, noLabel, noX, bandTop);
}

function drawTableFieldRow(
  row: Extract<LayoutRow, { type: 'tableFieldRow' }>,
  page: PDFPage,
  form: PDFForm,
  fonts: Fonts,
  topY: number,
): void {
  const cells = answerCells(row.scope, row.answerColumns);

  // Cell frames.
  drawCellBorder(page, MARGIN, topY, LABEL_COL, row.height);

  for (const cell of cells) {
    drawCellBorder(page, cell.x, topY, cell.width, row.height);
  }

  // Label stack (label + PDF hints) in the label column.
  const labelBottom = drawCellLines(
    row.labelLines,
    page,
    fonts,
    MARGIN + CELL_PAD,
    topY - CELL_PAD,
  );

  const field = { key: row.fieldKey, scope: row.scope };
  const widgetTop = topY - CELL_PAD;

  switch (row.kind) {
    case 'text':
    case 'number':
    case 'date': {
      for (const cell of cells) {
        const textField = form.createTextField(
          acroFieldName(field, cell.column),
        );

        textField.addToPage(page, {
          x: cell.x + CELL_PAD,
          y: widgetTop - TEXT_FIELD_HEIGHT,
          width: cell.width - 2 * CELL_PAD,
          height: TEXT_FIELD_HEIGHT,
        });
        // Auto-size (Spec 2026-07-13 §2a): DA `/Helvetica 0 Tf` – iOS/viewers
        // shrink the glyphs to the field width instead of clipping at ~8 chars.
        // MUST run AFTER addToPage (pdf-lib throws on a page-less field).
        textField.setFontSize(0);
      }

      return;
    }

    case 'checkbox': {
      // §2d: former `/Btn` checkbox → single `/Tx` X-field (same field name).
      for (const cell of cells) {
        createXField(
          form,
          page,
          acroFieldName(field, cell.column),
          cell.x + CELL_PAD,
          widgetTop - WIDGET_LINE_HEIGHT + 1.5,
        );
      }

      return;
    }

    case 'yesno': {
      // §2d: former radio group → a PAIR of `/Tx` X-fields, named via the
      // established option helper (`${key}__yes` / `${key}__no` + suffix).
      for (const cell of cells) {
        drawYesNo(
          page,
          form,
          fonts,
          acroOptionFieldName(field, 'yes', cell.column),
          acroOptionFieldName(field, 'no', cell.column),
          cell.x + CELL_PAD,
          widgetTop,
          row.yesnoStacked ?? false,
          row.yesLabel ?? 'Ja',
          row.noLabel ?? 'Nein',
        );
      }

      return;
    }

    case 'checkbox_group': {
      // Options band starts below the label stack; option labels once in the
      // label column, answer cells only X-fields, line-aligned (R3b, §2d).
      let bandTop = labelBottom;

      for (const option of row.optionRows ?? []) {
        drawCellLines(
          option.lines,
          page,
          fonts,
          MARGIN + CELL_PAD + 8,
          bandTop,
        );

        for (const cell of cells) {
          createXField(
            form,
            page,
            acroOptionFieldName(field, option.optionKey, cell.column),
            cell.x + CELL_PAD,
            bandTop - WIDGET_LINE_HEIGHT + 1.5,
          );
        }

        bandTop -= option.rowHeight;
      }

      return;
    }
  }
}

function drawRow(
  row: LayoutRow,
  page: PDFPage,
  form: PDFForm,
  fonts: Fonts,
  topY: number,
): void {
  switch (row.type) {
    case 'text': {
      page.drawText(row.text, {
        x: MARGIN,
        y: topY - row.size,
        size: row.size,
        font: fontFor(fonts, row.font),
        color: row.color,
      });

      return;
    }

    case 'gap':
      return;

    case 'sectionHeadingRow': {
      const bottom = drawCellLines(row.lines, page, fonts, MARGIN, topY);

      page.drawLine({
        start: { x: MARGIN, y: bottom + 2 },
        end: { x: MARGIN + CONTENT_WIDTH, y: bottom + 2 },
        thickness: 0.75,
        color: TEXT_COLOR,
      });

      return;
    }

    case 'tableHeadRow': {
      const colWidth = answerColumnWidth(row.answerColumnCount);

      drawCellBorder(page, MARGIN, topY, LABEL_COL, row.height);

      row.columns.forEach((lines, index) => {
        const x = MARGIN + LABEL_COL + index * colWidth;

        drawCellBorder(page, x, topY, colWidth, row.height);
        drawCellLines(lines, page, fonts, x + CELL_PAD, topY - CELL_PAD);
      });

      return;
    }

    case 'tableFieldRow': {
      drawTableFieldRow(row, page, form, fonts, topY);

      return;
    }

    case 'textFieldRow': {
      const fieldBottom = topY - row.height; // LABEL_TO_FIELD_GAP sits on top
      const textField = form.createTextField(row.fieldName);

      if (row.multiline) {
        textField.enableMultiline();
      }

      textField.addToPage(page, {
        x: MARGIN,
        y: fieldBottom,
        width: CONTENT_WIDTH,
        height: row.fieldHeight,
      });
      // Auto-size after addToPage (Spec 2026-07-13 §2a) – applies to the
      // single-line full-width long fields AND the multiline textarea.
      textField.setFontSize(0);

      return;
    }

    case 'personSignatureRow': {
      const fieldBottomY = topY - TEXT_FIELD_HEIGHT;
      const placeDateField = form.createTextField(row.fieldName);

      // Fillable place/date field (left column).
      placeDateField.addToPage(page, {
        x: MARGIN,
        y: fieldBottomY,
        width: 220,
        height: TEXT_FIELD_HEIGHT,
      });
      placeDateField.setFontSize(0); // Auto-size (Spec 2026-07-13 §2a)

      // Signature area (Spec 2026-07-13 §2c): NO `/FT /Sig` field – the real
      // handwritten signature is added via iOS Markup, which works on ANY PDF
      // regardless of form fields (the empty /Sig widget was a certificate
      // field that looked like a text box and never triggered the finger-
      // signature dialog on iPhone). We draw a plain page-content line the user
      // signs onto; the "Unterschrift …" label sits below it. Deterministic:
      // constant coordinates, no time/RNG/IDs.
      page.drawLine({
        start: { x: MARGIN + 260, y: fieldBottomY },
        end: { x: MARGIN + CONTENT_WIDTH, y: fieldBottomY },
        thickness: 1,
        color: SIGNATURE_LINE_COLOR,
      });

      drawCellLines(row.placeDateLines, page, fonts, MARGIN, fieldBottomY - 2);
      drawCellLines(
        row.signatureLines,
        page,
        fonts,
        MARGIN + 260,
        fieldBottomY - 2,
      );

      return;
    }
  }
}

function drawFragment(
  fragment: PlacedBlockFragment,
  page: PDFPage,
  form: PDFForm,
  fonts: Fonts,
): void {
  let y = fragment.topY;

  for (const row of fragment.rows) {
    drawRow(row, page, form, fonts, y);
    y -= row.height;
  }
}

// ── Public entry points ───────────────────────────────────────────────────────

async function computePlacedLayout(
  config: SelfDisclosureConfig,
  opts: RenderSelfDisclosureOptions,
  doc: PDFDocument,
): Promise<{ placed: PlacedLayout; fonts: Fonts }> {
  const content = buildRenderContent(config, opts);

  const fonts: Fonts = {
    regular: await doc.embedFont(StandardFonts.Helvetica),
    bold: await doc.embedFont(StandardFonts.HelveticaBold),
    italic: await doc.embedFont(StandardFonts.HelveticaOblique),
  };

  const placed = placeLayoutItems(buildLayoutItems(content, fonts));

  return { placed, fonts };
}

/**
 * Measure/placement phase with full fragment detail (rows included), without
 * drawing anything. Same code path as `renderSelfDisclosurePdf` up to (and
 * including) placement – used by the layout tests to check the §2.5 height
 * formulas and to measure every rendered line against its column width.
 */
export async function layoutDocumentDetailed(
  config: SelfDisclosureConfig,
  opts: RenderSelfDisclosureOptions = {},
): Promise<PlacedLayout> {
  const parsed = SelfDisclosureConfigSchema.parse(config);
  const doc = await PDFDocument.create({ updateMetadata: false });
  const { placed } = await computePlacedLayout(parsed, opts, doc);

  return placed;
}

export interface LayoutDocumentEntry {
  blockId: string;
  page: number;
  topY: number;
  height: number;
}

/**
 * Testable layout export (Sanierungs-Spec §11.1.2): every placed block
 * fragment with page + topY + height, no drawing. Invariant under test:
 * `topY - height >= MARGIN` for every fragment (no block crosses a page).
 */
export async function layoutDocument(
  config: SelfDisclosureConfig,
  opts: RenderSelfDisclosureOptions = {},
): Promise<LayoutDocumentEntry[]> {
  const { fragments } = await layoutDocumentDetailed(config, opts);

  return fragments.map(({ blockId, page, topY, height }) => ({
    blockId,
    page,
    topY,
    height,
  }));
}

/**
 * Renders a fillable, deterministic Mieterselbstauskunft PDF from a config.
 * The config is re-validated against the schema at the render boundary.
 */
export async function renderSelfDisclosurePdf(
  config: SelfDisclosureConfig,
  opts: RenderSelfDisclosureOptions = {},
): Promise<Uint8Array> {
  const parsed = SelfDisclosureConfigSchema.parse(config);
  const now = opts.now ?? DETERMINISTIC_EPOCH;

  const doc = await PDFDocument.create({ updateMetadata: false });
  const { placed, fonts } = await computePlacedLayout(parsed, opts, doc);

  const form = doc.getForm();
  const pages: PDFPage[] = [];

  for (let i = 0; i < placed.pageCount; i += 1) {
    pages.push(doc.addPage([PAGE_WIDTH, PAGE_HEIGHT]));
  }

  for (const fragment of placed.fragments) {
    const page = pages[fragment.page - 1];

    if (!page) continue; // structurally impossible; defensive

    drawFragment(fragment, page, form, fonts);
  }

  // NeedAppearances (Spec 2026-07-13 §2a): make viewers regenerate field
  // appearances on input – this is what lets iOS/QuickLook re-render the
  // auto-sized text as the user types (the reference PDF got it for free via a
  // macOS-Quartz re-save; pdf-lib's updateFieldAppearances sets neither this
  // nor /DR, so we set it explicitly). Deterministic constant.
  form.acroForm.dict.set(PDFName.of('NeedAppearances'), PDFBool.True);

  // Metadata / determinism
  doc.setTitle(
    parsed.language === 'en' ? DOCUMENT_TITLE.en : DOCUMENT_TITLE.de,
  );
  doc.setAuthor(parsed.sender.displayName);
  // White-label (Spec 2026-07-08 PR A / A-T1): Creator + Producer are
  // deliberately NOT set. `PDFDocument.create({ updateMetadata: false })`
  // suppresses pdf-lib's auto-stamp, so omitting the calls leaves the Info
  // dictionary WITHOUT /Creator and /Producer keys – no "pdf-lib" or "Locari"
  // marker leaks into the distributed bytes. (Empirically verified: the empty-
  // string variant would instead write empty `()` Info entries – also clean,
  // but the omit variant is tidier and equally byte-deterministic.)
  // Machine-readable config fingerprint v2 (E2E validates downloads).
  // The `guarantor` key is emitted ONLY when true (§2.4/8, F2): configs without
  // a guarantor – including every legacy config – stay byte-identical to today.
  doc.setSubject(
    JSON.stringify({
      v: 2,
      lang: parsed.language,
      persons: parsed.personCount,
      ...(parsed.includeGuarantor ? { guarantor: true } : {}),
      fields: parsed.enabledFields,
    }),
  );
  doc.setCreationDate(now);
  doc.setModificationDate(now);

  // updateFieldAppearances:false (Spec 2026-07-13 §2a/§2d) – pdf-lib otherwise
  // re-bakes each field's appearance on save AND rewrites the `/DA` auto-size
  // `/Helvetica 0 Tf` back to a concrete font size, defeating setFontSize(0).
  // We instead ship `0 Tf` + NeedAppearances=true and let the viewer generate
  // appearances (and honour each widget's `/MK`+`/BS` border). Deterministic.
  return doc.save({ useObjectStreams: false, updateFieldAppearances: false });
}
